import BaseInput, { BaseInputProps } from '../BaseInput/BaseInput';
import { useEffect, useState } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { FieldPath, FieldValues } from 'react-hook-form/dist/types';
import 'scss/Main.scss';
import { toFloat } from '../../utils/number';

export type BaseValidatedInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
    BaseInputProps<TFieldValues>,
    'onChange' | 'name' | 'value' | 'disabled' | 'checked' | 'required'
> & {
    rules?: RegisterOptions;
    name: TName;
    disabled?: ((formState: FieldValues) => boolean) | boolean;
    maskProps?: {
        changeMask?(value: string | null | undefined): string;
    };
    numberFormatLocale?: string;
    enableValidationOnChange?: boolean;
};

const BaseValidatedInput = <
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    id,
    name,
    rules,
    disabled,
    type,
    maskProps,
    numberFormatLocale,
    enableValidationOnChange,
    ...props
}: BaseValidatedInputProps<TFieldValues, TName>) => {
    const { register, unregister, control, getValues, trigger } = useFormContext<TFieldValues>();

    useEffect(() => {
        register(name);
        return unregister(name);
    }, []);

    const isCheckBox = type === 'checkbox' || type === 'radio' || type === 'switch';

    const defaultMask = () => {
        if (maskProps?.changeMask) return maskProps.changeMask(getValues(name));

        return maskProps?.mask ?? '';
    };

    const [mask, setMask] = useState(defaultMask());

    const handleOnAccept = (value: string, onAcceptFn?: (value: string) => void) => {
        onAcceptFn && onAcceptFn(value);
    };
    const handleChangeMask = (value: string, changeMaskFn?: (value: string) => string) => {
        changeMaskFn && setMask(changeMaskFn(value));
    };

    const shouldIncludeChar = (char: string, index: number, maskChar: string) => {
        const isValueChar = mask[index] === '9' || mask[index] === 'a' || mask[index] === '*';
        const isMaskChar = char === maskChar;
        return (isValueChar || maskProps?.preserveMask) && !isMaskChar;
    };

    const getInputValue = (value: any, type?: string) => (type === 'file' ? undefined : value);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        onChange: (...event: any[]) => void
    ) => {
        if (numberFormatLocale) {
            handleNumberFormatChange(e, onChange);
        } else {
            handleDefaultChange(e, onChange);
        }
        if (enableValidationOnChange) {
            trigger(name);
        }
    };
    const handleNumberFormatChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        onChange: (...event: any[]) => void
    ) => {
        const tempNumber = toFloat(e.target.value.replace(/[^0-9,.]/g, ''));
        onChange(tempNumber ? tempNumber.toLocaleString(numberFormatLocale) : '0');
    };
    const handleDefaultChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        onChange: (...event: any[]) => void
    ) => {
        let value;
        switch (type) {
            case 'number':
                value = toFloat(e.target.value) ?? null;
                break;

            case 'file':
                value = e.target.files;
                break;

            case 'checkbox':
                value = e.target.checked;
                break;

            default:
                value = e;
        }

        onChange(value);
    };
    return (
        <Controller<TFieldValues, TName>
            name={name}
            control={control}
            render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                const handleAccept = (value: string) => {
                    if (maskProps) {
                        handleOnAccept(value, maskProps.onAccept);
                        handleChangeMask(value, maskProps.changeMask);
                    }
                };

                const maskChar = maskProps?.maskPlaceholder || maskProps?.maskChar || '_';

                const unmaskValue = (value: string) => {
                    return Array.from(value).reduce((unmasked, char, i) => {
                        if (shouldIncludeChar(char, i, maskChar)) {
                            unmasked += char;
                        }
                        return unmasked;
                    }, '');
                };

                const inputMaskProps = maskProps && {
                    maskProps: {
                        onAccept: handleAccept,
                        ...maskProps,
                        mask: mask,
                    },
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        onChange(unmaskValue(e.target.value));
                    },
                };

                return (
                    <BaseInput<TFieldValues>
                        id={id}
                        type={type}
                        value={getInputValue(value, type)}
                        checked={isCheckBox ? value : undefined}
                        onChange={(e) => handleInputChange(e, onChange)}
                        invalidMessage={error?.message}
                        invalid={!!error?.message}
                        disabled={typeof disabled === 'function' ? disabled(getValues()) : disabled}
                        refCallback={ref}
                        {...props}
                        {...inputMaskProps}
                    />
                );
            }}
            rules={rules as any}
        />
    );
};

export default BaseValidatedInput;
